import { CalendarDays } from 'lucide-react';

import type { GuideModule } from '../types';

/**
 * `apps/web-admin/src/routes/calendar/`: button labels and states quoted
 * verbatim from `calendar-list-page.tsx` and `holidays/holiday-list-page.tsx`.
 * `requiredPermission: 'menu.calendar.manage'` already confirmed by
 * `menu-calendar/design.md` §0.3/§6.1 (`tasks.md` PR 2 note), no
 * re-verification needed.
 */
export const CALENDAR_MODULE: GuideModule = {
  id: 'calendar',
  title: 'Calendario',
  icon: CalendarDays,
  requiredPermission: 'menu.calendar.manage',
  priority: 6,
  summary:
    'El Calendario planifica qué menú se ofrece cada fecha en cada posición del menú, con qué cupo máximo de unidades. La grilla tiene una fila por fecha y una columna por posición activa, agrupadas por tipo de servicio: cada celda vacía muestra "Asignar" para cargar un plato en esa posición y esa fecha, restringido a platos de su mismo tipo de servicio. Mientras una entrada no está publicada, es solo un borrador interno: recién cuando la publicás queda visible para que se pida. También administra los feriados: fechas excepcionales que pueden tener o no servicio, y que pueden aplicar a una zona en particular en vez de a todas. Si al publicar una fecha falta cobertura de alguna etiqueta dietaria importante, el sistema te avisa con una advertencia, pero no te bloquea la publicación.',
  steps: [
    {
      title: 'Planificar la semana',
      description:
        'Andá a Calendario, elegí el rango de fechas con los filtros y hacé clic en "Nueva entrada" para cada posición y fecha que quieras ofrecer, indicando el cupo máximo; o hacé clic directamente en "Asignar" dentro de una celda vacía de la grilla, que ya viene con esa posición y esa fecha preseleccionadas. La grilla solo te deja elegir platos del mismo tipo de servicio que la posición de la columna.',
      keyTerms: ['entrada-de-calendario', 'cupo', 'posicion-del-menu'],
    },
    {
      title: 'Publicar una entrada',
      description:
        'En la grilla, hacé clic en el control de publicación de la entrada. Si falta cobertura dietaria para esa fecha, se abre un aviso: hacé clic en "Publicar de todas formas" para continuar, o "Cancelar" para revisar antes.',
      keyTerms: ['publicado', 'advertencia-dietaria'],
    },
    {
      title: 'Copiar la planificación de una semana a otra',
      description:
        'Hacé clic en "Copiar semana", elegí el rango de origen y el desplazamiento de días, y hacé clic para previsualizar antes de aplicar la copia. La previsualización detecta el conflicto por fecha y posición, no solo por plato; si una posición de origen fue desactivada desde que se cargó esa entrada, la fila se marca "Posición inactiva, se omite" y no se copia.',
      keyTerms: ['copiar-semana', 'posicion-del-menu'],
    },
    {
      title: 'Gestionar feriados por zona',
      description:
        'Hacé clic en "Feriados", después en "Nuevo feriado", y elegí si aplica a todas las zonas o a una zona en particular, y si ese día tiene servicio o no.',
      keyTerms: ['feriado', 'feriado-de-zona'],
    },
    {
      title: 'Revisar el aviso de opciones dietarias',
      description:
        'La grilla marca con un ícono las fechas donde falta cobertura de alguna etiqueta dietaria importante, antes incluso de intentar publicar.',
      keyTerms: ['advertencia-dietaria', 'etiqueta-dietaria'],
    },
  ],
  states: [
    {
      value: 'DRAFT',
      label: 'Borrador',
      description: 'La entrada todavía no está publicada: no es visible para el cliente.',
    },
    {
      value: 'PUBLISHED',
      label: 'Publicado',
      description: 'La entrada ya está visible y se puede pedir.',
    },
  ],
  faq: [
    {
      question: '¿Un feriado de zona reemplaza a un feriado general de la misma fecha?',
      answer:
        'Sí: si hay un feriado de zona y uno general para la misma fecha, el de zona tiene prioridad.',
    },
    {
      question: '¿La advertencia dietaria me impide publicar una fecha?',
      answer:
        'No. Es un aviso, no un bloqueo. Podés hacer clic en "Publicar de todas formas" si decidís continuar igual.',
    },
    {
      question: '¿"Copiar semana" sobrescribe entradas que ya existen en el destino?',
      answer:
        'Depende de la casilla "Sobrescribir conflictos": si la marcás, se reemplazan; si no, se dejan como están. Siempre podés previsualizar el resultado antes de confirmar.',
    },
    {
      question: '¿Puedo despublicar una entrada que ya está publicada?',
      answer: 'Sí, hacé clic en el mismo control de publicación para volverla a borrador.',
    },
  ],
  tips: [
    'Si una fecha te falta cargar un plato en alguna posición, la celda vacía te lo recuerda con el botón "Asignar": no hace falta ir a buscar "Nueva entrada" cada vez.',
    'Cargá los feriados de zona con anticipación: afectan la disponibilidad que ve el cliente en el calendario.',
    'Revisá el ícono de advertencia dietaria antes de publicar toda la semana, no fecha por fecha.',
    'Usá "Copiar semana" para semanas con planificación repetitiva y ajustá después solo lo que cambia.',
    'Publicá con margen antes de la hora de corte del contrato correspondiente, para que el cliente tenga tiempo de pedir.',
  ],
};
